/**
 * Client portal layout (Reppr Elite, phase 7) — light theme.
 *
 * Desktop: sticky sidebar per the portal mock — agency lockup, athlete
 * identity, grouped nav (Money / In progress / Records) with live counts,
 * agent of record at the foot. Mobile: top bar + bottom nav.
 *
 * Real behaviour preserved: agency white-label branding (logo, name,
 * portal colour), feature toggles (show_contracts / show_incentives),
 * unread + action counts, push notifications, welcome modal, install
 * banner. The agent-of-record block uses the real primary agent from
 * getClientContactInfo — nothing invented.
 */

import { useState, useEffect } from 'react'
import { Outlet, NavLink, Link, useNavigate } from 'react-router-dom'
import { useClientSession } from '../hooks/useClientSession'
import {
  getUnreadMessageCount,
  getClientActionItemSummary,
  getClientProfile,
  getClientContactInfo,
} from '../api/clientPortal'
import { usePushNotifications } from '../hooks/usePushNotifications'
import { usePortalTheme } from '../hooks/usePortalTheme'
import PortalInstallBanner from './PortalInstallBanner'
import PortalPreviewBanner from './PortalPreviewBanner'
import { isPreview } from '../api/clientPortal/previewMode'
import { PortalWelcome } from './PortalWelcome'
import '../pages/portal/portal-theme.css'
import styles from './PortalShell.module.css'

interface NavEntry {
  to: string
  label: string
  count?: number
  show?: boolean
}

function initialsOf(name: string | null | undefined, fallback: string): string {
  if (!name) return fallback
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
}

/**
 * Bottom-nav icons.
 *
 * Six destinations at ~10px of text on a small phone was a row of near-
 * illegible words. An icon carries the destination at a glance and the label
 * stays underneath to say which one it is — neither alone is enough.
 *
 * Line icons at 1.5px on currentColor, so the active state inherits the
 * agency's own portal colour without any of them being redrawn.
 */
const NAV_ICON_PROPS = {
  width: 19,
  height: 19,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
}

/** A statement: ruled lines on a page. */
const StatementIcon = () => (
  <svg {...NAV_ICON_PROPS}><path d="M5 3h14v18H5z" /><path d="M9 8h6M9 12h6M9 16h3" /></svg>
)
/** A season: a calendar. */
const SeasonIcon = () => (
  <svg {...NAV_ICON_PROPS}><rect x="3" y="5" width="18" height="16" rx="1" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>
)
/** A deal: a handshake reduced to two meeting strokes. */
const DealsIcon = () => (
  <svg {...NAV_ICON_PROPS}><path d="M3 12l4-4 5 4 4-4 5 4" /><path d="M3 12v5a1 1 0 001 1h16a1 1 0 001-1v-5" /></svg>
)
/** Needs you: something waiting to be ticked. */
const ActionsIcon = () => (
  <svg {...NAV_ICON_PROPS}><path d="M9 11l2 2 4-4" /><rect x="3" y="4" width="18" height="16" rx="1" /></svg>
)
/** Messages: a speech bubble. */
const MessagesIcon = () => (
  <svg {...NAV_ICON_PROPS}><path d="M21 15a2 2 0 01-2 2H8l-4 4V5a2 2 0 012-2h13a2 2 0 012 2z" /></svg>
)
/** Records: a folder. */
const RecordsIcon = () => (
  <svg {...NAV_ICON_PROPS}><path d="M3 7a1 1 0 011-1h5l2 2h9a1 1 0 011 1v9a1 1 0 01-1 1H4a1 1 0 01-1-1z" /></svg>
)

export default function ClientPortalLayout() {
  const { user, logout } = useClientSession()
  const navigate = useNavigate()
  const [unreadCount, setUnreadCount] = useState(0)
  const [actionCount, setActionCount] = useState(0)
  const [agencyBranding, setAgencyBranding] = useState<{ name: string; logo: string | null } | null>(null)
  const [athlete, setAthlete] = useState<{ name: string | null; position: string | null; team: string | null; headshot: string | null } | null>(null)
  const [agent, setAgent] = useState<{ name: string } | null>(null)
  const [showWelcome, setShowWelcome] = useState(false)
  // An agent looking in must not register THEIR device to receive THIS
  // athlete's notifications.
  usePushNotifications({ enabled: !isPreview() })
  usePortalTheme() // light theme and the portal manifest

  // Load agency branding, athlete identity and agent of record
  useEffect(() => {
    getClientProfile().then(profile => {
      if (profile?.expand?.agency_id) {
        const agency = profile.expand.agency_id
        setAgencyBranding({
          name: agency.name ?? '',
          logo: agency.logo || null,
        })
        // Agency white-label colour overrides the portal accent
        if (agency.portal_primary_color) {
          document.documentElement.style.setProperty('--pt-accent', agency.portal_primary_color)
          document.documentElement.style.setProperty('--portal-brand', agency.portal_primary_color)
        }
      }
      if (profile?.expand?.client_id) {
        setAthlete({
          name: profile.expand.client_id.full_name || null,
          position: profile.expand.client_id.position || null,
          team: profile.expand.client_id.team_name || null,
          headshot: profile.expand.client_id.headshot_url || null,
        })
      }
    }).catch((err) => {
      console.error('Failed to load client profile for portal layout:', err)
    })

    getClientContactInfo().then(info => {
      if (info.primary_agent?.full_name) {
        setAgent({ name: info.primary_agent.full_name })
      }
    }).catch(err => console.error('[portal] agent of record failed to load:', err))
  }, [])

  // Show welcome modal on first login (once per user)
  useEffect(() => {
    // Not for an agent looking in: the welcome is addressed to the athlete,
    // and dismissing it here would mark it seen for someone who never saw it.
    if (!user?.id || isPreview()) return
    const key = `portal_welcome_seen_${user.id}`
    if (!localStorage.getItem(key)) {
      setShowWelcome(true)
    }
  }, [user?.id])

  // Unread messages + pending action items, refreshed every minute
  useEffect(() => {
    async function fetchCounts() {
      const [messageCount, actionSummary] = await Promise.all([
        getUnreadMessageCount(),
        getClientActionItemSummary(),
      ])
      setUnreadCount(messageCount)
      setActionCount(actionSummary.total_pending)
    }
    fetchCounts()
    const interval = setInterval(fetchCounts, 60000)
    return () => clearInterval(interval)
  }, [])

  function handleLogout() {
    const clientId = isPreview() ? user?.client_id : null
    logout()
    navigate(clientId ? `/clients/${clientId}` : '/portal/login')
  }

  function handleWelcomeDismiss() {
    if (user?.id) {
      localStorage.setItem(`portal_welcome_seen_${user.id}`, 'true')
    }
    setShowWelcome(false)
  }

  const athleteInitials = initialsOf(user?.name, user?.email?.[0]?.toUpperCase() || '?')
  // The athlete's own headshot first, then whatever the login record carries.
  // A parent or guardian following an athlete keeps the login avatar.
  const isAthleteLogin = !user?.relationship || user.relationship === 'athlete'
  const avatarSrc = (isAthleteLogin && athlete?.headshot) || user?.avatar || null
  /**
   * Whose portal this is.
   *
   * For the athlete it is their own position and team, as before. For a parent
   * or guardian it names the athlete instead, because someone following a
   * family member must never have to work out whose figures they are reading —
   * and a household with two athletes would otherwise be genuinely ambiguous.
   */
  const isFollowing = !!user?.relationship && user.relationship !== 'athlete'
  const identityRole = isFollowing
    ? `${user.relationship[0].toUpperCase()}${user.relationship.slice(1)}${athlete?.name ? ` · ${athlete.name}` : ''}`
    : [athlete?.position, athlete?.team].filter(Boolean).join(' · ')

  const groups: { label: string; items: NavEntry[] }[] = [
    {
      label: 'Money',
      items: [
        { to: '/portal/finances', label: 'Your statement' },
        { to: '/portal/contracts', label: 'Contracts', show: !!user?.show_contracts },
        { to: '/portal/incentives', label: 'Incentives', show: !!user?.show_incentives },
      ],
    },
    {
      label: 'In progress',
      items: [
        // The season sits at the top of this group because it is the one
        // thing here that is true on a day with no deal and no message.
        { to: '/portal/schedule', label: 'Your season' },
        { to: '/portal/deals', label: 'Deals in motion' },
        { to: '/portal/actions', label: 'Needs you', count: actionCount },
        { to: '/portal/messages', label: 'Messages', count: unreadCount },
      ],
    },
    {
      label: 'Records',
      items: [
        { to: '/portal/documents', label: 'Documents' },
        { to: '/portal/compliance', label: 'Filings' },
        { to: '/portal/vendors', label: 'Your team' },
        { to: '/portal/profile', label: 'Your details' },
      ],
    },
  ]

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `${styles.navItem} ${isActive ? styles.navItemActive : ''}`

  const bottomNavClass = ({ isActive }: { isActive: boolean }) =>
    `${styles.bottomNavItem} ${isActive ? styles.bottomNavItemActive : ''}`

  return (
    <>
      {/* Above the layout, not inside it: .layout is a flex row, and a third
          child there would sit beside the sidebar instead of over both. */}
      <PortalPreviewBanner />
      <div className={`portalLight ${styles.layout}`}>
      {/* The welcome names the agency. It waits for the real name rather than
          greeting the athlete on behalf of "Your Agency". */}
      {showWelcome && user && agencyBranding?.name && (
        <PortalWelcome
          user={user}
          agencyName={agencyBranding.name}
          onDismiss={handleWelcomeDismiss}
        />
      )}

      <aside className={styles.sidebar}>
        <Link to="/portal/finances" className={styles.lockup}>
          <img
            src={agencyBranding?.logo || '/icon-512.png'}
            alt=""
            className={styles.lockupLogo}
          />
          <span className={styles.lockupText}>
            <span className={styles.lockupName}>{agencyBranding?.name || 'Reppr'}</span>
            <span className={styles.lockupSub}>Client portal</span>
          </span>
        </Link>

        <Link to="/portal/profile" className={styles.identity}>
          <span className={styles.identityAvatar}>
            {avatarSrc ? <img src={avatarSrc} alt="" loading="lazy" /> : athleteInitials}
          </span>
          <span className={styles.identityText}>
            <span className={styles.identityName}>{user?.name || user?.email}</span>
            {identityRole && <span className={styles.identityRole}>{identityRole}</span>}
          </span>
        </Link>

        <nav className={styles.nav} aria-label="Portal">
          {groups.map(group => {
            const items = group.items.filter(it => it.show !== false)
            if (items.length === 0) return null
            return (
              <div key={group.label} className={styles.navGroup}>
                <div className={styles.navGroupLabel}>{group.label}</div>
                {items.map(item => (
                  <NavLink key={item.to} to={item.to} className={navClass}>
                    <span className={styles.navLabel}>{item.label}</span>
                    {!!item.count && item.count > 0 && (
                      <span className={styles.navCount}>{item.count}</span>
                    )}
                  </NavLink>
                ))}
              </div>
            )
          })}
        </nav>

        {agent && (
          <div className={styles.agentBlock}>
            <div className={styles.agentBlockLabel}>Your agent of record</div>
            <div className={styles.agentRow}>
              <span className={styles.agentAvatar}>{initialsOf(agent.name, '·')}</span>
              <span className={styles.agentText}>
                <span className={styles.agentName}>{agent.name}</span>
                <Link to="/portal/messages" className={styles.agentAction}>Send a message</Link>
              </span>
            </div>
          </div>
        )}

        <button onClick={handleLogout} className={styles.signOut}>
          {isPreview() ? 'Leave preview' : 'Sign out'}
        </button>
      </aside>

      <div className={styles.main}>
        {/* Mobile top bar */}
        <div className={styles.mobileBar}>
          <Link to="/portal/finances" className={styles.mobileBarName}>
            {agencyBranding?.name || 'Client portal'}
          </Link>
          <div className={styles.mobileBarRight}>
            <Link to="/portal/profile" className={styles.mobileAvatar}>
              {avatarSrc ? <img src={avatarSrc} alt="" loading="lazy" /> : athleteInitials}
            </Link>
          </div>
        </div>

        <Outlet />

        {!isPreview() && (
          <PortalInstallBanner
            agencyName={agencyBranding?.name}
            agencyLogo={agencyBranding?.logo}
          />
        )}

        {/* Mobile bottom nav. There is no sidebar on a phone, so anything
            missing from this bar is reachable only by typing a URL. */}
        <nav className={styles.bottomNav} aria-label="Portal">
          {[
            { to: '/portal/finances', label: 'Statement', Icon: StatementIcon },
            { to: '/portal/schedule', label: 'Season', Icon: SeasonIcon },
            { to: '/portal/deals', label: 'Deals', Icon: DealsIcon },
            { to: '/portal/actions', label: 'Needs you', Icon: ActionsIcon, count: actionCount },
            { to: '/portal/messages', label: 'Messages', Icon: MessagesIcon, count: unreadCount },
            { to: '/portal/documents', label: 'Records', Icon: RecordsIcon },
          ].map(({ to, label, Icon, count }) => (
            <NavLink key={to} to={to} className={bottomNavClass}>
              <Icon />
              <span>{label}</span>
              {!!count && count > 0 && (
                <span className={styles.bottomNavBadge}>{count > 9 ? '9+' : count}</span>
              )}
            </NavLink>
          ))}
        </nav>
        </div>
      </div>
    </>
  )
}
